import { z } from 'zod';

import { ApiError } from '@mds/shared/lib/api';
import { answerProblems, answerText, type AnswerDrafts } from '@mds/shared/lib/form-answers';
import type { AdminEvent } from '@mds/shared/types/event';
import type {
  AdminEventRegistration,
  EventCheckinRequest,
  EventPaymentMethod,
  EventRegistrationAnswer,
} from '@mds/shared/types/event-registration';
import type { FormQuestionSet } from '@mds/shared/types/form-question';

import { stamp } from '@mds/shared/lib/dates';

import { localKey, type QuestionSetDraft } from '@/domain/form-builder';
import { formatCents } from '@/lib/money';
import {
  optionalEmail,
  optionalText,
  picked,
  requiredEmail,
  requiredPhone,
  requiredText,
  wholeNumberText,
} from '@/lib/validation';

/**
 * The vocabulary the attendee list and the door share: how a registration's
 * state reads, what the door typed or scanned, and the blocked-action codes the
 * registration endpoints answer with.
 */

/* -------------------------------------------------------------- reading --- */

/** A registration's state, with the classes to draw it in. */
export const registrationTone = (r: Pick<AdminEventRegistration, 'status' | 'checkedIn'>) =>
  r.status === 'cancelled'
    ? { label: 'Cancelled', cls: 'border-hairline bg-raised text-ink-muted' }
    : r.status === 'waitlisted'
      ? { label: 'Waitlist', cls: 'border-dashed border-ink/25 bg-raised text-ink' }
      : r.status === 'payment_pending'
        ? { label: 'Payment due', cls: 'border-warn-edge bg-warn-wash text-warn-text' }
        : r.checkedIn
          ? { label: 'Checked in', cls: 'border-brand-edge/35 bg-brand-wash text-brand-strong' }
          : { label: 'Confirmed', cls: 'border-mint/35 bg-mint-wash text-mint-deep' };

export const seatsLabel = (seats: number) => `${seats} seat${seats === 1 ? '' : 's'}`;

const METHODS: Record<EventPaymentMethod, string> = {
  cash: 'Cash',
  whish: 'Whish',
  omt: 'OMT',
  other: 'Other',
};

export const EVENT_PAYMENT_METHOD_OPTIONS = (Object.keys(METHODS) as EventPaymentMethod[]).map(
  (value) => ({ value, label: METHODS[value] }),
);

/* ---------------------------------------------------------------- forms --- */

/**
 * Adding a party at the desk. Mirrors `createAdminEventRegistrationSchema`,
 * plus the service's rule that a priced event emails the ticket, so it needs an
 * address. Too many seats is not an error: a party that does not fit joins the
 * waitlist, which `arrivesWaitlisted` tells the form in advance. Staff answer
 * the event's registration form exactly as a guest does, required questions
 * included; each problem is filed under `answers.<questionId>`.
 */
export const registrationSchema = (event: Pick<AdminEvent, 'price'>, form: FormQuestionSet) =>
  z.object({
    attendeeName: requiredText(160),
    attendeeEmail: event.price.amountMinor === 0 ? optionalEmail : requiredEmail,
    attendeePhone: requiredPhone,
    seatCount: wholeNumberText({ min: 1, max: 10_000 }),
    answers: z.custom<AnswerDrafts>().superRefine((answers, ctx) => {
      for (const [questionId, message] of Object.entries(answerProblems(form, answers))) {
        ctx.addIssue({ code: 'custom', path: [questionId], message });
      }
    }),
  });

/** Seats confirmed registrations leave free. Payment-pending parties hold none. */
export const seatsFree = (event: Pick<AdminEvent, 'capacity' | 'registeredSeats'>) =>
  Math.max(0, event.capacity - event.registeredSeats);

/** Whether a new party of this size would go on the waitlist rather than in. */
export const arrivesWaitlisted = (
  event: Pick<AdminEvent, 'capacity' | 'registeredSeats'>,
  seats: number,
) => Number.isInteger(seats) && seats > 0 && seats > seatsFree(event);

/**
 * Taking a party's full payment. Mirrors `recordEventRegistrationPaymentSchema`:
 * the typed method is required for `other` and ignored for the rest.
 */
export const eventPaymentSchema = z
  .object({
    method: picked('Choose how it was paid.'),
    methodDetail: z.string(),
    note: optionalText(500),
  })
  .superRefine((draft, ctx) => {
    if (draft.method !== 'other') return;
    const detail = requiredText(80).safeParse(draft.methodDetail);
    if (!detail.success) {
      ctx.addIssue({
        code: 'custom',
        path: ['methodDetail'],
        message:
          draft.methodDetail.trim() === ''
            ? 'Type how the money was taken, or pick one of the other methods.'
            : (detail.error.issues[0]?.message ?? ''),
      });
    }
  });

/** Cancelling a party. Mirrors `cancelEventRegistrationSchema`. */
export const cancelRegistrationSchema = z.object({ reason: optionalText(500) });

/* ----------------------------------------------------------------- door --- */

const TICKET_CODE = /^MDS-[0-9]{4,}$/i;
/** The QR payload is 32 random bytes as base64url: exactly 43 characters, no spaces. */
const QR_TOKEN = /^[A-Za-z0-9_-]{43}$/;

/**
 * What the door typed or scanned, as the check-in request it stands for, or
 * null when it is neither — a name, which the caller matches against the list.
 *
 * A handheld scanner types the QR payload and presses Enter, so it arrives
 * through the same box as a typed ticket code; the two are told apart by shape.
 */
export function checkinRequestOf(raw: string): EventCheckinRequest | null {
  const value = raw.trim();
  if (TICKET_CODE.test(value)) return { ticketCode: value.toUpperCase() };
  if (QR_TOKEN.test(value)) return { qrToken: value };
  return null;
}

/* --------------------------------------------------------------- errors --- */

const errorCode = (error: unknown): string | null =>
  error instanceof ApiError ? (error.code ?? null) : null;

/** Registration failures, phrased for the administrator. */
/** The registration form was saved again since this copy was loaded. */
export const REGISTRATION_FORM_CHANGED = 'EVENT_REGISTRATION_FORM_CHANGED';

/**
 * What a new event's registration form asks before anyone edits it — the same two questions the
 * API writes when a create request carries no form (`DEFAULT_REGISTRATION_QUESTIONS` on the
 * backend), so the create modal starts from them rather than from nothing.
 */
export const defaultRegistrationDraft = (): QuestionSetDraft => ({
  ungrouped: [
    { key: localKey(), label: 'What do you do?', type: 'text', options: '', required: false },
    {
      key: localKey(),
      label: 'How did you hear about this?',
      type: 'select',
      options: ['Instagram', 'Facebook', 'A friend', 'Walked past', 'University', 'Other'].join(
        '\n',
      ),
      required: false,
    },
  ],
  sections: [],
});

/** A stored answer as a question and a line of text, whichever shape it was stored in. */
export const answerLine = (answer: EventRegistrationAnswer) =>
  'questionId' in answer
    ? { question: answer.label, answer: answerText(answer.value) }
    : { question: answer.question, answer: answer.answer };

/**
 * One registration's details as labelled lines, in reading order; its status,
 * seats, amount and ticket are drawn as chips above them. The detail dialog,
 * its PDF and its Excel export all read from here, so the three never disagree.
 */
export function registrationFacts(r: AdminEventRegistration): { label: string; value: string }[] {
  const facts: { label: string; value: string }[] = [
    { label: 'Email', value: r.attendeeEmail || '—' },
    { label: 'Phone', value: r.attendeePhone || '—' },
    { label: 'Registered', value: stamp(r.createdAt) },
  ];
  const add = (label: string, value: string | null | false | undefined) => {
    if (value) facts.push({ label, value });
  };
  add('Waitlisted', r.waitlistedAt && stamp(r.waitlistedAt));
  add('Confirmed', r.confirmedAt && stamp(r.confirmedAt));
  add('Checked in', r.checkedInAt && stamp(r.checkedInAt));
  // A waitlisted party owes nothing until it is promoted.
  add(
    'Balance',
    r.price.totalAmountMinor > 0 &&
      r.status !== 'waitlisted' &&
      formatCents(r.payment.balanceAmountMinor),
  );
  const record = r.payment.record;
  add(
    'Paid',
    record &&
      [
        formatCents(record.amountMinor),
        record.methodLabel,
        record.reference,
        stamp(record.recordedAt),
        record.note,
      ]
        .filter(Boolean)
        .join(' · '),
  );
  add('Ticket revoked', r.ticket?.revokedAt && stamp(r.ticket.revokedAt));
  add('Cancelled', r.cancelledAt && stamp(r.cancelledAt));
  add('Reason', r.cancellationReason);
  add('Mailing list', r.mailingListOptIn && 'Joined when registering');
  return facts;
}

export function registrationProblem(error: unknown, fallback: string): string {
  switch (errorCode(error)) {
    case 'EVENT_CAPACITY_EXCEEDED':
      return 'There are not enough seats free for this party yet.';
    case 'EVENT_REGISTRATION_WAITLISTED':
      return 'This party is on the waitlist. Promote them before recording a payment.';
    case 'EVENT_REGISTRATION_NOT_WAITLISTED':
      return 'This party is no longer on the waitlist.';
    case 'EVENT_REGISTRATION_NOT_PAYMENT_PENDING':
      return 'Only a party that has not paid yet can be moved to the waitlist.';
    case 'EVENT_REGISTRATION_ALREADY_PAID':
      return 'This registration is already paid.';
    case 'EVENT_REGISTRATION_CANCELLED':
      return 'This registration was cancelled, so nothing more can be recorded against it.';
    case 'EVENT_REGISTRATION_ALREADY_CANCELLED':
      return 'This registration is already cancelled.';
    case REGISTRATION_FORM_CHANGED:
      return 'The registration questions changed while this was open. They have been reloaded. Check the answers and register again.';
    default:
      return error instanceof ApiError ? error.message : fallback;
  }
}
